import React from "react";
import { Link } from "react-router-dom";
import { useLocaleContext } from "../context/LocaleContext";
import { Image, Sparkles, ArrowRight, Shield, Zap } from "lucide-react";

/**
 * GeminiImageBanner - SSR-safe promotional banner for Gemini Image Watermark Remover
 * Links to the /remove-gemini-image-watermark page with localized URLs
 */
export const GeminiImageBanner: React.FC = () => {
  const { locale } = useLocaleContext();

  // Localized URLs from hreflang.ts
  const localizedUrls: Record<string, string> = {
    en: "/remove-gemini-image-watermark",
    de: "/de/gemini-bild-wasserzeichen-entfernen",
    "pt-BR": "/pt-br/remover-marca-dagua-imagem-gemini",
    "ms-MY": "/my/buang-tanda-air-imej-gemini",
    es: "/es/eliminar-marca-agua-imagen-gemini",
    fr: "/fr/supprimer-filigrane-image-gemini",
    ja: "/ja/gemini-image-watermark-remover",
    id: "/id/hapus-watermark-gambar-gemini",
  };

  // Localized content
  const content: Record<
    string,
    {
      badge: string;
      title: string;
      description: string;
      features: string[];
      cta: string;
    }
  > = {
    en: {
      badge: "New!",
      title: "Remove Gemini Image Watermarks for free",
      description:
        "Clean AI-generated images from Google Gemini. Remove the visible watermark and strip EXIF metadata including GPS location - 100% free and private. SynthID embedded in the image pixels is not affected.",
      features: [
        "Remove visible watermarks",
        "Strip all metadata",
        "100% private",
      ],
      cta: "Clean Images Now",
    },
    de: {
      badge: "Neu!",
      title: "Gemini Bild-Wasserzeichen kostenlos entfernen",
      description:
        "Bereinigen Sie KI-generierte Bilder von Google Gemini. Entfernen Sie das sichtbare Wasserzeichen und EXIF-Metadaten einschließlich GPS-Standort - 100% kostenlos und privat. SynthID in den Bildpixeln bleibt davon unberührt.",
      features: [
        "Sichtbare Wasserzeichen entfernen",
        "Alle Metadaten löschen",
        "100% privat",
      ],
      cta: "Bilder bereinigen",
    },
    "pt-BR": {
      badge: "Novo!",
      title: "Remover Marcas d'Água de Imagem Gemini",
      description:
        "Limpe imagens geradas por IA do Google Gemini. Remova a marca d'água visível e metadados EXIF incluindo localização GPS - 100% gratuito e privado. O SynthID incorporado nos pixels da imagem não é afetado.",
      features: ["Remover marcas visíveis", "Limpar metadados", "100% privado"],
      cta: "Limpar Imagens",
    },
    "ms-MY": {
      badge: "Ciri!",
      title: "Buang Tanda Air Imej Gemini",
      description:
        "Bersihkan imej dijana AI daripada Google Gemini. Buang tanda air yang boleh dilihat dan metadata EXIF termasuk lokasi GPS - 100% percuma dan peribadi. SynthID yang terbenam dalam piksel imej tidak terjejas.",
      features: ["Buang tanda air", "Buang semua metadata", "100% peribadi"],
      cta: "Bersih Imej Sekarang",
    },
    es: {
      badge: "Nueva!",
      title: "Eliminar Marcas de Agua de Imagen Gemini",
      description:
        "Limpia imágenes generadas por IA de Google Gemini. Elimina la marca de agua visible y metadatos EXIF incluyendo ubicación GPS - 100% gratis y privado. El SynthID incrustado en los píxeles de la imagen no se ve afectado.",
      features: [
        "Eliminar marcas visibles",
        "Limpiar metadatos",
        "100% privado",
      ],
      cta: "Limpiar Imágenes",
    },
    fr: {
      badge: "Nouveau !",
      title: "Supprimer les Filigranes d'Image Gemini",
      description:
        "Nettoyez les images générées par IA de Google Gemini. Supprimez le filigrane visible et les métadonnées EXIF y compris la localisation GPS - 100% gratuit et confidentiel. Le SynthID intégré dans les pixels de l'image n'est pas affecté.",
      features: [
        "Supprimer filigranes visibles",
        "Effacer les métadonnées",
        "100% confidentiel",
      ],
      cta: "Nettoyer les Images",
    },
    ja: {
      badge: "新機能！",
      title: "Gemini画像透かしを削除",
      description:
        "Google GeminiのAI生成画像をクリーニング。見える透かしを削除し、GPS位置情報を含むEXIFメタデータを除去 - 100%無料でプライベート。画像ピクセルに埋め込まれたSynthIDには影響しません。",
      features: [
        "可視透かしを削除",
        "メタデータを除去",
        "100%プライベート",
      ],
      cta: "画像をクリーニング",
    },
    id: {
      badge: "Baru!",
      title: "Hapus Watermark Gambar Gemini",
      description:
        "Bersihkan gambar yang dihasilkan AI dari Google Gemini. Hapus watermark yang terlihat dan metadata EXIF termasuk lokasi GPS - 100% gratis dan pribadi. SynthID yang tertanam pada piksel gambar tidak terpengaruh.",
      features: [
        "Hapus watermark terlihat",
        "Bersihkan metadata",
        "100% pribadi",
      ],
      cta: "Bersihkan Gambar",
    },
  };

  const t = content[locale] || content.en;
  const url = localizedUrls[locale] || localizedUrls.en;

  return (
    <section className="mt-16 mb-8">
      <Link to={url} className="block group" aria-label={t.title}>
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 dark:from-purple-900/20 dark:via-pink-900/20 dark:to-orange-900/20 border border-purple-200 dark:border-purple-800/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
          {/* Decorative gradient orbs */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-purple-400/20 to-pink-400/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-orange-400/20 to-purple-400/20 rounded-full blur-3xl"></div>

          <div className="relative p-6 md:p-8">
            <div className="flex flex-col md:flex-row md:items-center gap-6">
              {/* Left side - Icon and badge */}
              <div className="flex-shrink-0">
                <div className="relative">
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <Image className="w-8 h-8 md:w-10 md:h-10 text-white" />
                  </div>
                  {/* New badge */}
                  <div className="absolute -top-2 -right-2 px-2 py-0.5 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full text-white text-xs font-bold shadow-md">
                    {t.badge}
                  </div>
                </div>
              </div>

              {/* Middle - Content */}
              <div className="flex-1 min-w-0">
                <h3 className="text-xl md:text-2xl font-bold text-neutral-900 dark:text-white mb-2 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                  {t.title}
                </h3>
                <p className="text-neutral-600 dark:text-neutral-300 text-sm md:text-base mb-4 line-clamp-2">
                  {t.description}
                </p>

                {/* Feature pills */}
                <div className="flex flex-wrap gap-2">
                  {t.features.map((feature, index) => {
                    const icons = [Shield, Zap, Sparkles];
                    const Icon = icons[index % icons.length];
                    return (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/60 dark:bg-white/10 rounded-full text-xs font-medium text-purple-700 dark:text-purple-300 border border-purple-200/50 dark:border-purple-700/50"
                      >
                        <Icon size={12} />
                        {feature}
                      </span>
                    );
                  })}
                </div>
              </div>

              {/* Right side - CTA */}
              <div className="flex-shrink-0">
                <span className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-xl font-semibold text-sm md:text-base shadow-lg group-hover:shadow-xl transition-all duration-300">
                  {t.cta}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </div>
          </div>
        </div>
      </Link>
    </section>
  );
};

export default GeminiImageBanner;
